import React, { VoidFunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { prepareProps } from 'record-it-components/utils/propUtils';
import {
  SearchableSelect,
  SearchableSelectProps
} from 'record-it-components/input/SearchableSelect/SearchableSelect';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { UserGroup } from '../../../classes/entities/UserGroup/userGroupTypes';
import { UserGroupUtils } from '../../../classes/entities/UserGroup/UserGroupUtils';
import { useNullableSelectEntityItems } from '../../../hooks/entity/useNullableSelectEntityItems/useNullableSelectEntityItems';

export type UserGroupSelectProps = PropsWithoutChildren<
  Omit<
    SearchableSelectProps<UserGroup>,
    | 'getKeyOfItem'
    | 'getDisplayTextOfItem'
    | 'items'
    | 'value'
    | 'onValueChanged'
    | 'label'
  >
> & {
  /**
   * ID of the UserGroup selected by default.
   */
  readonly userGroupId: string | null;
  /**
   * Called whenever the user selects a new UserGroup.
   */
  readonly onUserGroupChanged: (value: string | null) => void;
  /**
   * UserGroups the user can select from.
   *
   * If you include `null`, the user will be able to select "no usergroup".
   */
  readonly availableUserGroups: Array<UserGroup | null>;
  /**
   * Label to show above the select component.
   *
   * @default "Benutzergruppe"
   */
  readonly label?: string;
};

export const UserGroupSelect: VoidFunctionComponent<UserGroupSelectProps> =
  React.memo((props) => {
    const {
      userGroupId,
      availableUserGroups,
      label,
      onUserGroupChanged,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });
    const { t } = useTranslation();

    const useSelectEntityItemsReturnValue = useNullableSelectEntityItems<
      UserGroup,
      string
    >({
      value: userGroupId,
      entities: availableUserGroups,
      entityNotFoundText: t(
        'components.input.UserGroupSelect.userGroupNotFound'
      ),
      nullValueText: t('components.input.UserGroupSelect.noUserGroup'),
      onValueChanged: onUserGroupChanged,
      getDisplayTextOfEntity: UserGroupUtils.displayTextGetter,
      getKeyOfEntity: UserGroupUtils.keyGetter,
      getValueOfEntity: (group) => group.id
    });

    return (
      <SearchableSelect
        label={
          label != null ? label : t('components.input.UserGroupSelect.label')
        }
        value={useSelectEntityItemsReturnValue.selectedItem}
        items={useSelectEntityItemsReturnValue.items}
        onValueChanged={useSelectEntityItemsReturnValue.onSelectedItemChanged}
        getDisplayTextOfItem={
          useSelectEntityItemsReturnValue.getDisplayTextOfItem
        }
        getKeyOfItem={useSelectEntityItemsReturnValue.getKeyOfItem}
        data-testid="UserGroupSelect.SearchableSelect"
        {...remainingProps}
      ></SearchableSelect>
    );
  });
